<template>
  <Dialog :open="isOpen" @update:open="closeModal">
    <DialogContent class="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Switch Expedition</DialogTitle>
      </DialogHeader>
      
      <div class="space-y-2">
        <p class="text-gray-600">
          {{ userName }}'s current expedition is 
          <span class="font-bold">{{ currentExpedition?.name }}</span>
        </p>
        <p class="text-gray-600">
          Select the expedition you want to switch {{ userName }} to:
        </p>
        <p class="text-gray-500">Only expeditions with the same path as {{ currentExpedition?.name }} are shown.</p>
        
        <div v-if="expeditionsStore.isLoading" class="flex justify-center">
          <Loader2 class="h-6 w-6 animate-spin" />
        </div>

        <div v-else-if="expeditionsStore.error" class="text-red-500">
          {{ expeditionsStore.error }}
        </div>

        <div v-else>
          <Select v-model="selectedExpeditionId">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Select an expedition" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem
                  v-for="expedition in filteredExpeditions"
                  :key="expedition.id"
                  :value="expedition.id.toString()"
                >
                  {{ expedition.name }}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>


        <div class="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            @click="closeModal"
          >
            Cancel
          </Button>
          <Button
            :disabled="!selectedExpeditionId || isSubmitting"
            @click="handleSubmit"
          >
            <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin" />
            Switch Expedition
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useExpeditionsStore } from '@/stores/expeditions'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'

const route = useRoute()
const toast = useToast()
const router = useRouter()
const props = defineProps<{
  isOpen: boolean
  sourceExpeditionId: string
  userId: string
  userName: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'success', data: any): void
}>()

const expeditionsStore = useExpeditionsStore()
const selectedExpeditionId = ref<string | null>(null)
const isSubmitting = ref(false)

const currentExpeditionId = route.params.id
const currentExpedition = computed(() => {
  return expeditionsStore.expeditions.find(
    (expedition) => expedition.id.toString() === currentExpeditionId
  )
})

onMounted(async () => {
  await expeditionsStore.fetchExpeditions()
})

const filteredExpeditions = computed(() => {
  const currentPath = currentExpedition.value?.path
  return expeditionsStore.expeditions.filter((expedition) => expedition.path === currentPath)
})

const closeModal = () => {
  selectedExpeditionId.value = null
  emit('close')
}


const handleSubmit = async () => {
  if (!selectedExpeditionId.value) return

  isSubmitting.value = true
  try {
    await api.post(
      API_ROUTES.SWITCH_USER_EXPEDITION(currentExpeditionId.toString()),
      {
        user_id: props.userId,
        new_expedition_id: selectedExpeditionId.value
      }
    )
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'User switched expedition successfully',
      life: 3000
    })
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push(`/manage/${selectedExpeditionId.value}`)
  } catch (err: any) {
    console.error('Failed to switch expedition:', err)
  } finally {
    isSubmitting.value = false
  }
}
</script> 